import React, { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, trackEvent } from "@/lib/apiClient";
import Layout, { useSEO } from "@/components/Layout";
import { Section, Overline, BlueRule, Button } from "@/components/primitives";

const CATEGORIES = ["General Inquiry", "Speaking", "Workshop", "Partnership", "Media", "Book Inquiry"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", category: "General Inquiry", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useSEO({ title: "Contact | Impactful Mindset", description: "Get in touch with Impactful Mindset for speaking, workshops, partnerships, media and general inquiries." });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      trackEvent("contact_submission", { category: form.category });
      setDone(true);
      toast.success("Thanks for reaching out.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <Layout>
      <Section className="pt-20 pb-24">
        <div className="max-w-2xl">
          <Overline>Contact</Overline>
          <h1 className="font-serif-display text-4xl sm:text-5xl font-bold mt-4 text-[#111827]">Get in touch</h1>
          <BlueRule className="mt-6" />
          <p className="text-lg text-slate-600 mt-6 leading-relaxed">
            Questions, speaking requests, workshops or partnerships—send a note and we'll be in touch.
          </p>
        </div>

        {done ? (
          <div data-testid="contact-success" className="max-w-2xl mt-10 border border-[#BFDBFE] bg-[#F8F9FA] p-8">
            <p className="font-serif-display text-2xl text-[#0F2C59]">Thank you.</p>
            <p className="text-slate-600 mt-2">Your message has been received. We'll respond as soon as we can.</p>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="contact-form" className="max-w-2xl mt-10 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <input data-testid="contact-name" required placeholder="Your name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full border border-slate-300 px-4 py-3 focus:outline-none focus:border-[#0F2C59]" />
              <input data-testid="contact-email" type="email" required placeholder="Email address" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full border border-slate-300 px-4 py-3 focus:outline-none focus:border-[#0F2C59]" />
            </div>
            <select data-testid="contact-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="w-full border border-slate-300 px-4 py-3 bg-white focus:outline-none focus:border-[#0F2C59]">
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
            <textarea data-testid="contact-message" required rows={6} placeholder="How can we help?" value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="w-full border border-slate-300 px-4 py-3 focus:outline-none focus:border-[#0F2C59]" />
            <Button type="submit" disabled={loading} data-testid="contact-submit">{loading ? "Sending…" : "Send Message"}</Button>
          </form>
        )}
      </Section>
    </Layout>
  );
}
